/**
 * useTwinCalibration (P180.4) -- the twin calibration report: modelled
 * recovery vs the actuals from closed incidents + completed drills.
 * Admin-gated.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { ApplyIbsCalibration, TwinCalibrationReport } from "shared/api/types";

export function useTwinCalibration(): UseQueryResult<TwinCalibrationReport, ApiError> {
  return useQuery({
    queryKey: ["admin", "twin-calibration"],
    queryFn: () => api.get<TwinCalibrationReport>("/v1/admin/twin/calibration"),
  });
}

export interface ApplyCalibrationResult {
  service_id: string;
  service_name: string;
  old_rto_hours: number | null;
  new_rto_hours: number;
  observed_mean_hours: number;
  sample_count: number;
  source: string;
  changed: boolean;
}

/** P181/P196: apply a service's observed recovery as its modelled RTO,
 *  from a chosen actuals source ("incidents" | "telemetry"). */
export type CalibrationSource = "incidents" | "telemetry";

export interface ApplyCalibrationVars {
  serviceId: string;
  source: CalibrationSource;
}

export function useApplyCalibration(): UseMutationResult<
  ApplyCalibrationResult,
  ApiError,
  ApplyCalibrationVars
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ serviceId, source }: ApplyCalibrationVars) =>
      api.post<ApplyCalibrationResult>(
        `/v1/admin/twin/calibration/apply/${encodeURIComponent(serviceId)}?source=${source}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "twin-calibration"] });
    },
  });
}

/** P228: apply an IBS's observed drill recovery as its per-IBS recovery
 *  override (recovery_factor_override). */
export function useApplyIbsCalibration(): UseMutationResult<
  ApplyIbsCalibration,
  ApiError,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (ibsId: string) =>
      api.post<ApplyIbsCalibration>(
        `/v1/admin/twin/calibration/apply-ibs/${encodeURIComponent(ibsId)}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "twin-calibration"] });
    },
  });
}
